import {
  Box,
  BoxProps,
  Button,
  Input,
  InputProps,
  List,
  ListItem,
  ListProps,
  Text,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import emailSpellChecker from "@zootools/email-spell-checker";
import { useTranslation } from "gatsby-plugin-react-i18next";
import React from "react";

import { trackEvent } from "@base/lib/tracking-lib";
import {
  EMAIL_SECOND_LEVEL_DOMAINS,
  EMAIL_TOP_LEVEL_DOMAINS,
  getEmailDomains,
} from "./domains";
import { sanitizeEmailInput } from "./validateEmail";

const MAX_DOMAIN_SUGGESTIONS = 4;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
`;

export type EmailInputHandle = {
  /**
   * Call before submitting the email. Returns true when the submit may
   * proceed. Returns false exactly once per typed value when a typo
   * suggestion exists — the suggestion is shown/emphasized instead, and the
   * next call with the same value proceeds ("I mean what I typed").
   */
  confirmSubmit: () => boolean;
};

export type EmailInputProps = {
  value: string;
  onChange: (value: string) => void;
  /** Identifies the surface in tracking events, e.g. "quiz" or "checkout-ensure-user-modal". */
  trackingContext: string;
  placeholder?: string;
  /** When true (and no typo suggestion takes priority) `errorMessage` is shown. */
  isInvalid?: boolean;
  errorMessage?: string;
  dataTestId?: string;
  /** Style overrides for the input. Event handlers here are overridden by the component's own. */
  inputProps?: InputProps;
  containerProps?: BoxProps;
  dropdownProps?: ListProps;
};

/**
 * Shared email input used by every email capture surface (quiz email slide,
 * checkout EnsureUserModal, landing waitlist). On top of a plain input it:
 * - suggests domain completions while the user types past the "@";
 * - runs a typo check on blur ("Did you mean vilius@gmail.com?");
 * - blocks a submit once per value via `confirmSubmit` (ref) so the typo
 *   suggestion can't be skipped by clicking the CTA while still focused;
 * - strips whitespace as the user types.
 *
 * Validation stays with the caller: pass `isInvalid` + `errorMessage`.
 */
export const EmailInput = React.forwardRef<EmailInputHandle, EmailInputProps>(
  function EmailInput(
    {
      value,
      onChange,
      trackingContext,
      placeholder,
      isInvalid,
      errorMessage,
      dataTestId,
      inputProps,
      containerProps,
      dropdownProps,
    },
    ref,
  ) {
    const { i18n, t } = useTranslation();
    const listboxId = React.useId();

    const [isFocused, setIsFocused] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const [isDropdownDismissed, setIsDropdownDismissed] = React.useState(false);
    const [typoSuggestion, setTypoSuggestion] = React.useState<string | null>(null);
    const [emphasisCount, setEmphasisCount] = React.useState(0);
    const shownTypoSuggestionsRef = React.useRef<Set<string>>(new Set());
    // The value whose submit was already blocked once; a repeat submit of the
    // same value proceeds — unless it arrives within the same gesture (funnel
    // NextButtons call submitQuestion twice per click). Reset on every edit.
    const blockedSubmitRef = React.useRef<{
      value: string;
      at: number;
      overriddenTracked: boolean;
    } | null>(null);

    const domains = React.useMemo(() => getEmailDomains(i18n.language), [i18n.language]);

    const atIndex = value.indexOf("@");
    const typedDomain = atIndex > 0 ? value.slice(atIndex + 1).toLowerCase() : null;

    const domainSuggestions = React.useMemo(() => {
      if (typedDomain == null) return [];
      return domains
        .filter((domain) => domain.startsWith(typedDomain) && domain !== typedDomain)
        .slice(0, MAX_DOMAIN_SUGGESTIONS);
    }, [domains, typedDomain]);

    const isDropdownOpen = isFocused && !isDropdownDismissed && domainSuggestions.length > 0;

    function computeTypoSuggestion(email: string): string | null {
      if (email.indexOf("@") <= 0) return null;

      const suggestion = emailSpellChecker.run({
        email,
        domains,
        secondLevelDomains: EMAIL_SECOND_LEVEL_DOMAINS,
        topLevelDomains: EMAIL_TOP_LEVEL_DOMAINS,
      });

      // Only surface corrections we can vouch for: the library's fallback
      // branches guess at domains outside our curated list (mycompany.cm →
      // mycompany.co, x@foo.co → aol.co), so those are dropped.
      if (!suggestion || !domains.includes(suggestion.domain)) return null;
      return suggestion.full === email ? null : suggestion.full;
    }

    function trackSuggestionShown(suggestion: string) {
      if (shownTypoSuggestionsRef.current.has(suggestion)) return;
      shownTypoSuggestionsRef.current.add(suggestion);
      trackEvent({
        name: "email-typo-suggestion-shown",
        properties: { context: trackingContext, suggestion },
      });
    }

    React.useImperativeHandle(ref, () => ({
      confirmSubmit(): boolean {
        const suggestion = computeTypoSuggestion(value);
        if (!suggestion) return true;

        const blocked = blockedSubmitRef.current;
        if (blocked && blocked.value === value) {
          // A repeat within ~one gesture is the same click arriving twice
          // (funnel NextButtons call submitQuestion on top of the slide's own
          // handler) — keep blocking. Only a later, separate press means
          // "keep what I typed".
          if (Date.now() - blocked.at < 500) {
            return false;
          }
          if (!blocked.overriddenTracked) {
            blocked.overriddenTracked = true;
            trackEvent({
              name: "email-typo-suggestion-overridden",
              properties: { context: trackingContext, suggestion },
            });
          }
          return true;
        }

        blockedSubmitRef.current = { value, at: Date.now(), overriddenTracked: false };
        setTypoSuggestion(suggestion);
        setEmphasisCount((count) => count + 1);
        trackSuggestionShown(suggestion);
        trackEvent({
          name: "email-typo-suggestion-blocked-submit",
          properties: { context: trackingContext, suggestion },
        });
        return false;
      },
    }));

    function handleChange(rawValue: string) {
      const nextValue = sanitizeEmailInput(rawValue);
      setActiveIndex(-1);
      setIsDropdownDismissed(false);
      setTypoSuggestion(null);
      blockedSubmitRef.current = null;
      onChange(nextValue);
    }

    function acceptDomainSuggestion(domain: string) {
      onChange(value.slice(0, atIndex + 1) + domain);
      setActiveIndex(-1);
      setTypoSuggestion(null);
      blockedSubmitRef.current = null;
      trackEvent({
        name: "email-domain-autocomplete-accepted",
        properties: { context: trackingContext, suggestion: domain },
      });
    }

    function acceptTypoSuggestion(suggestion: string) {
      onChange(suggestion);
      setTypoSuggestion(null);
      blockedSubmitRef.current = null;
      trackEvent({
        name: "email-typo-suggestion-accepted",
        properties: { context: trackingContext, suggestion },
      });
    }

    function handleBlur() {
      setIsFocused(false);
      setActiveIndex(-1);

      const suggestion = computeTypoSuggestion(value);
      setTypoSuggestion(suggestion);
      if (suggestion) {
        trackSuggestionShown(suggestion);
      }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (!isDropdownOpen) return;

      const count = domainSuggestions.length;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((idx) => (idx + 1) % count);
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((idx) => (idx <= 0 ? count - 1 : idx - 1));
          break;
        case "Enter":
        case "Tab":
          if (activeIndex >= 0 && activeIndex < count) {
            e.preventDefault();
            acceptDomainSuggestion(domainSuggestions[activeIndex]);
          }
          break;
        case "Escape":
          setIsDropdownDismissed(true);
          setActiveIndex(-1);
          break;
      }
    }

    const showTypoSuggestion = typoSuggestion != null && typoSuggestion !== value;
    const showError = !showTypoSuggestion && isInvalid && !!errorMessage;

    return (
      <Box position="relative" pb={7} {...containerProps}>
        <Box position="relative">
          <Input
            size="lg"
            type="email"
            inputMode="email"
            autoComplete="email"
            name="email"
            autoCapitalize="off"
            spellCheck="false"
            placeholder={placeholder}
            {...inputProps}
            data-testid={dataTestId}
            value={value}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isDropdownOpen}
            aria-controls={isDropdownOpen ? listboxId : undefined}
            aria-activedescendant={
              isDropdownOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
            }
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />

          {isDropdownOpen && (
            <List
              id={listboxId}
              role="listbox"
              position="absolute"
              top="calc(100% + 4px)"
              left={0}
              right={0}
              zIndex={20}
              backgroundColor="white"
              color="gray.800"
              borderRadius="md"
              boxShadow="lg"
              py={1}
              maxH="240px"
              overflowY="auto"
              {...dropdownProps}
            >
              {domainSuggestions.map((domain, idx) => (
                <ListItem
                  key={domain}
                  id={`${listboxId}-option-${idx}`}
                  role="option"
                  aria-selected={idx === activeIndex}
                  px={4}
                  py={2}
                  fontSize="sm"
                  cursor="pointer"
                  backgroundColor={idx === activeIndex ? "gray.100" : undefined}
                  _hover={{ backgroundColor: "gray.100" }}
                  // preventDefault keeps the input focused so blur doesn't fire before click
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => acceptDomainSuggestion(domain)}
                >
                  {value.slice(0, atIndex + 1)}
                  <Box as="span" fontWeight="semibold">
                    {domain}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Box position="absolute" bottom={0} left={0} right={0} aria-live="polite">
          {showTypoSuggestion ? (
            <Button
              key={emphasisCount}
              variant="link"
              size="sm"
              fontWeight="semibold"
              color="orange.400"
              whiteSpace="normal"
              textAlign="left"
              textDecoration="underline"
              animation={
                emphasisCount > 0 ? `${shakeAnimation} 0.4s ease-in-out` : undefined
              }
              onClick={() => acceptTypoSuggestion(typoSuggestion)}
              data-testid="email-typo-suggestion"
            >
              {t("emailLib.didYouMean", {
                defaultValue: "Did you mean {{email}}?",
                email: typoSuggestion,
              })}
            </Button>
          ) : showError ? (
            <Text color="red.400" fontSize="sm">
              {errorMessage}
            </Text>
          ) : null}
        </Box>
      </Box>
    );
  },
);
